import {
  acknowledgementForExecution,
  shouldSpeakAcknowledgementForPrompt,
} from "./tts-acknowledgements";
type SessionOrigin = "chat" | "heartbeat";

export const ACKNOWLEDGEMENT_TTS_DELAY_MS = 500;
export const LIVE_TALK_ACKNOWLEDGEMENT_DELAY_MS = 200;
const MAX_ACKNOWLEDGEMENT_HISTORY = 512;

export type AcknowledgementKind = "deterministic" | "live_talk";

interface PendingAcknowledgement {
  kind: AcknowledgementKind;
  timer: number;
}

interface ScheduleAcknowledgementOptions {
  executionId: string;
  origin?: SessionOrigin;
  promptText?: string;
  kind: AcknowledgementKind;
  suppress?: boolean;
  bypassDeterministicEnablement?: boolean;
  delayMs: number;
  isEligible?: () => boolean;
  onReady: (text: string) => void;
}

export interface TtsAcknowledgementActions {
  speakAcknowledgement: (
    executionId: string,
    origin?: SessionOrigin,
    promptText?: string,
  ) => void;
  speakLiveTalkAcknowledgement: (
    executionId: string,
    suppressAcknowledgement: boolean,
  ) => void;
  cancelAcknowledgement: (executionId: string) => void;
  cancelLiveTalkAcknowledgements: () => void;
}

const pending = new Map<string, PendingAcknowledgement>();
const acknowledgedExecutions = new Set<string>();
const supersededExecutions = new Set<string>();
let deterministicAcknowledgementsEnabled = false;

function rememberBounded(history: Set<string>, executionId: string): void {
  history.add(executionId);
  while (history.size > MAX_ACKNOWLEDGEMENT_HISTORY) {
    const oldest = history.values().next().value;
    if (oldest === undefined) break;
    history.delete(oldest);
  }
}

export function scheduleTtsAcknowledgement(
  options: ScheduleAcknowledgementOptions,
): boolean {
  const existing = pending.get(options.executionId);
  const upgradesDeterministicPending =
    options.kind === "live_talk" && existing?.kind === "deterministic";
  if (
    (!options.bypassDeterministicEnablement &&
      !deterministicAcknowledgementsEnabled) ||
    options.origin === "heartbeat" ||
    (acknowledgedExecutions.has(options.executionId) &&
      !upgradesDeterministicPending) ||
    supersededExecutions.has(options.executionId)
  ) {
    return false;
  }
  if (existing) window.clearTimeout(existing.timer);
  cancelPendingTtsAcknowledgements();
  rememberBounded(acknowledgedExecutions, options.executionId);
  if (
    options.suppress ||
    (options.kind === "deterministic" &&
      !shouldSpeakAcknowledgementForPrompt(options.promptText))
  ) {
    return false;
  }
  const timer = window.setTimeout(() => {
    pending.delete(options.executionId);
    if (options.isEligible && !options.isEligible()) return;
    options.onReady(acknowledgementForExecution(options.executionId));
  }, options.delayMs);
  pending.set(options.executionId, { kind: options.kind, timer });
  return true;
}

export function supersedeTtsAcknowledgement(executionId: string): void {
  const entry = pending.get(executionId);
  if (entry) {
    window.clearTimeout(entry.timer);
    pending.delete(executionId);
  }
  rememberBounded(supersededExecutions, executionId);
}

export function cancelPendingTtsAcknowledgements(
  kind?: AcknowledgementKind,
): void {
  for (const [executionId, entry] of pending) {
    if (kind && entry.kind !== kind) continue;
    window.clearTimeout(entry.timer);
    pending.delete(executionId);
  }
}

export function setDeterministicAcknowledgementsEnabledForTests(
  enabled: boolean,
): void {
  deterministicAcknowledgementsEnabled = enabled;
}

export function resetTtsAcknowledgementSchedulerForTests(): void {
  cancelPendingTtsAcknowledgements();
  acknowledgedExecutions.clear();
  supersededExecutions.clear();
  deterministicAcknowledgementsEnabled = false;
}

export function createTtsAcknowledgementActions(hooks: {
  onReady: (
    executionId: string,
    text: string,
    kind: AcknowledgementKind,
  ) => void;
  onScheduled: () => void;
  onCancelled: (executionId: string) => void;
  onKindCancelled: (kind: AcknowledgementKind) => void;
  isLiveTalkEligible: () => boolean;
}): TtsAcknowledgementActions {
  const schedule = (
    options: Omit<ScheduleAcknowledgementOptions, "onReady">,
  ) => {
    const scheduled = scheduleTtsAcknowledgement({
      ...options,
      onReady: (text) => hooks.onReady(options.executionId, text, options.kind),
    });
    if (scheduled) hooks.onScheduled();
  };
  return {
    speakAcknowledgement: (executionId, origin, promptText) =>
      schedule({
        executionId,
        origin,
        promptText,
        kind: "deterministic",
        delayMs: ACKNOWLEDGEMENT_TTS_DELAY_MS,
      }),
    speakLiveTalkAcknowledgement: (executionId, suppress) =>
      schedule({
        executionId,
        kind: "live_talk",
        suppress,
        bypassDeterministicEnablement: true,
        delayMs: LIVE_TALK_ACKNOWLEDGEMENT_DELAY_MS,
        isEligible: hooks.isLiveTalkEligible,
      }),
    cancelAcknowledgement: (executionId) => {
      supersedeTtsAcknowledgement(executionId);
      hooks.onCancelled(executionId);
    },
    cancelLiveTalkAcknowledgements: () => {
      cancelPendingTtsAcknowledgements("live_talk");
      hooks.onKindCancelled("live_talk");
    },
  };
}
